import { useState, useRef } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { Toast } from '../../../components';
import { DevelopmentPlanData, PlanAttachment } from '../types';
import { talentSpecializationsApi, talentProjectsApi, talentAttachmentsApi } from '../../../services/apiService';

const FOREGROUND = '#414042';
const BORDER_COLOR = '#e2e8f0';
const MUTED_BG = '#f1f5f9';

interface DevelopmentPlanProps {
  plan: DevelopmentPlanData;
  titleOverride?: string;
  entityId?: number;
  entityType?: 'specialization' | 'project';
  planFileName?: string;
  planAttachments?: PlanAttachment[];
  onAttachmentsChanged?: () => void;
  readOnly?: boolean;
}

export default function DevelopmentPlan({
  plan, titleOverride, entityId, entityType, planFileName,
  planAttachments = [], onAttachmentsChanged, readOnly,
}: DevelopmentPlanProps) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [uploading, setUploading] = useState(false);
  const [deleteAttId, setDeleteAttId] = useState<number | null>(null);
  const [files, setFiles] = useState<PlanAttachment[]>(planAttachments);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const api = entityType === 'project' ? talentProjectsApi : talentSpecializationsApi;
      const newAttachment = await api.uploadPlanAttachment(entityId!, file);
      setFiles(prev => [newAttachment, ...prev]);
      onAttachmentsChanged?.();
      setToastType('success');
      setToastMsg(isRTL ? 'تم رفع الملف بنجاح' : 'File uploaded successfully');
    } catch (err) {
      console.log('[DevelopmentPlan] upload failed:', err);
      setToastType('error');
      setToastMsg(isRTL ? 'فشل في رفع الملف' : 'Upload failed');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const planInputId = `plan-file-input-${entityType}-${entityId || 'none'}`;

  const handleDownloadAll = () => {
    if (files.length === 0) {
      setToastType('error');
      setToastMsg(isRTL ? 'لا توجد ملفات للتحميل' : 'No files to download');
      return;
    }
    for (const att of files) {
      if (att.file) {
        const a = document.createElement('a');
        a.href = att.file;
        a.download = att.title || 'file';
        a.target = '_blank';
        a.click();
      }
    }
  };

  const handleRemoveAttachment = (attachmentId: number) => {
    setDeleteAttId(attachmentId);
  };

  const confirmRemoveAttachment = async () => {
    if (deleteAttId === null) return;
    try {
      await talentAttachmentsApi.delete(deleteAttId);
      setFiles(prev => prev.filter(f => f.id !== deleteAttId));
      onAttachmentsChanged?.();
      setToastType('success');
      setToastMsg(isRTL ? 'تم حذف الملف' : 'File removed');
    } catch (err) {
      console.log('[DevelopmentPlan] remove failed:', err);
      setToastType('error');
      setToastMsg(isRTL ? 'فشل في حذف الملف' : 'Failed to remove file');
    }
    setDeleteAttId(null);
  };

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: colors.card, borderColor: BORDER_COLOR },
        createShadow(0, 1, 3, '#000', 0.1),
      ]}
    >
      {/* Header */}
      <View style={[styles.header, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        <View style={{ flex: 1 }}>
          <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
            {titleOverride || (isRTL ? 'خطة متابعة وتنمية الطلبة' : 'Student Development Plan')}
          </Text>
        </View>
        <View style={[styles.actionsRow, { flexDirection: isRTL ? 'row' : 'row-reverse' }]}>
          <TouchableOpacity
            style={[styles.actionBtn, createShadow(0, 1, 2, '#000', 0.05)]}
            onPress={handleDownloadAll}
          >
            <Text style={styles.actionText}>
              {isRTL ? 'تحميل الملفات' : 'Download Files'}
            </Text>
            <Ionicons name="download-outline" size={13} color={FOREGROUND} />
          </TouchableOpacity>
          {!readOnly && (Platform.OS === 'web' && entityId ? (
            <div style={{
              position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6,
              border: '1px solid #CBD5E1', borderRadius: 8,
              paddingLeft: 8, paddingRight: 8, paddingTop: 3, paddingBottom: 3,
              backgroundColor: 'rgba(255,255,255,0.1)', minHeight: 24, cursor: 'pointer',
              opacity: uploading ? 0.5 : 1,
            }}>
              <input
                ref={fileInputRef as any}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileChange}
                disabled={uploading}
                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 1 }}
              />
              <span style={{ fontSize: 12, fontFamily: FONTS.regular, color: FOREGROUND, pointerEvents: 'none' }}>
                {uploading ? (isRTL ? 'جاري الرفع...' : 'Uploading...') : (isRTL ? 'رفع ملف' : 'Upload File')}
              </span>
              <Ionicons name="cloud-upload-outline" size={13} color={FOREGROUND} />
            </div>
          ) : (
            <View style={[styles.actionBtn, uploading && { opacity: 0.5 }]}>
              <Text style={styles.actionText}>
                {uploading ? (isRTL ? 'جاري الرفع...' : 'Uploading...') : (isRTL ? 'رفع ملف' : 'Upload File')}
              </Text>
              <Ionicons name="cloud-upload-outline" size={13} color={FOREGROUND} />
            </View>
          ))}
        </View>
      </View>

      {/* File Cards Area */}
      {files.length > 0 && (
        <View style={styles.filesArea}>
          <View style={styles.filesRow}>
            {files.map((att) => (
              <TouchableOpacity
                key={att.id}
                style={styles.fileCard}
                onPress={() => {
                  if (att.file) {
                    const a = document.createElement('a');
                    a.href = att.file;
                    a.download = att.title || 'file';
                    a.target = '_blank';
                    a.click();
                  }
                }}
                activeOpacity={0.7}
              >
                <View style={styles.fileCardHeader}>
                  {!readOnly && (
                    <TouchableOpacity
                      onPress={(e) => { e.stopPropagation(); handleRemoveAttachment(att.id); }}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Ionicons name="close" size={18} color="#64748b" />
                    </TouchableOpacity>
                  )}
                  <Ionicons name="document-text-outline" size={36} color="#94a3b8" />
                </View>
                <View style={styles.fileCardBody}>
                  <Text style={styles.fileCardName} numberOfLines={3}>
                    {att.title}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      <Modal visible={deleteAttId !== null} transparent animationType="fade" onRequestClose={() => setDeleteAttId(null)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 12, padding: 24, width: 340, alignItems: 'center' }}>
            <Text style={{ fontSize: 16, fontFamily: FONTS.medium, color: colors.textPrimary, marginBottom: 12 }}>
              {isRTL ? 'تأكيد الحذف' : 'Confirm Delete'}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: 'center', marginBottom: 20 }}>
              {isRTL ? 'هل أنت متأكد من حذف هذا الملف؟' : 'Are you sure you want to delete this file?'}
            </Text>
            <View style={{ flexDirection: 'row', gap: 12, width: '100%' }}>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setDeleteAttId(null)}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }} onPress={confirmRemoveAttachment}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'حذف' : 'Delete'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      <Toast visible={!!toastMsg} message={toastMsg} type={toastType} onHide={() => { setToastMsg(''); setToastType('info'); }} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  header: {
    alignItems: 'center',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: 'rgba(0,0,0,0.8)',
  },
  actionsRow: {
    alignItems: 'center',
    gap: 8,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: 'rgba(255,255,255,0.1)',
    minHeight: 24,
  },
  actionText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  filesArea: {
    backgroundColor: MUTED_BG,
    borderRadius: 8,
    padding: 12,
  },
  filesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 20,
  },
  fileCard: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: BORDER_COLOR,
    borderRadius: 8,
    padding: 12,
    width: 200,
    gap: 8,
  },
  fileCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  fileCardBody: {
    alignItems: 'flex-end',
  },
  fileCardName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
    lineHeight: 20,
  },
});
